import { RANK_CONFIG } from "@/src/constants/post.constant";
import { RankedLeaderboardEntry } from "@/src/models/leaderboard.model";
import { useLeaderboardSummaryQuery } from "@/src/services/api/leaderboard/leaderboard.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import { MedalStar, Ranking } from "iconsax-react-nativejs";
import { useTranslation } from "react-i18next";
import { Pressable, Text, View } from "react-native";
import BlocksAvatar from "@/src/components/ui-kit/blocks-avatar";
import { formatCompactNumber } from "@/src/utils/number.utils";
import { Bone, SkeletonPulse } from "@/src/components/ui-kit/skeleton";

// Rank badge heights from RANK_CONFIG (h-14 / h-11 / h-9), in podium order.
const WIDGET_SKELETON_RANKS = [
  { rank: 1, avatar: "h-12 w-12", badge: "h-14" },
  { rank: 2, avatar: "h-11 w-11", badge: "h-11" },
  { rank: 3, avatar: "h-9 w-9", badge: "h-9" },
] as const;

/** Mirrors the widget body: top-3 podium, divider, then the "You" row. */
const LeaderboardWidgetSkeleton = () => (
  <SkeletonPulse testID="leaderboard-widget-skeleton">
    <View className="flex-row justify-between items-end px-1 mb-4">
      {WIDGET_SKELETON_RANKS.map(({ rank, avatar, badge }) => (
        <View key={rank} className="flex-1 items-center justify-end px-1 gap-1.5">
          <Bone className={`rounded-full ${avatar}`} />
          <Bone className="h-3 w-14" />
          <Bone className="h-3 w-8" />
          <Bone className={`w-10 rounded-xl ${badge}`} />
        </View>
      ))}
    </View>

    <View className="h-px bg-ash-3 mb-3" />

    <View className="flex-row items-center justify-between bg-ash border border-ash-3 rounded-xl p-3 gap-2">
      <View className="flex-row items-center gap-2 flex-1">
        <Bone className="h-9 w-9 rounded-full" />
        <Bone className="h-3 w-10" />
      </View>
      <View className="flex-row items-center gap-3">
        <View className="gap-1">
          <Bone className="h-2 w-10" />
          <Bone className="h-4 w-12" />
        </View>
        <View className="w-px h-8 bg-ash-3" />
        <View className="gap-1">
          <Bone className="h-2 w-8" />
          <Bone className="h-4 w-10" />
        </View>
      </View>
    </View>
  </SkeletonPulse>
);

type LeaderboardWidgetProps = Readonly<{
  onSeeAll?: () => void;
}>;

type TopUserCardProps = Readonly<{
  entry: RankedLeaderboardEntry;
}>;

const TopUserCard = ({ entry }: TopUserCardProps) => {
  const config = RANK_CONFIG[entry.rank as keyof typeof RANK_CONFIG];
  // Compact rather than fully written out: this sits in a one-line cell in a
  // three-across podium, so "1,234,567" ellipsises away to nothing useful.
  const formattedPoints = formatCompactNumber(entry?.points);

  if (!config) return null;

  const avatarSize = config.avatarSize;

  return (
    <View className="flex-1 items-center justify-end px-1 min-w-0">
      <BlocksAvatar
        userId={entry?.userId}
        fallbackImageUrl={entry?.avatar}
        name={entry?.displayName}
        size={avatarSize}
        className="border-2 border-ash-3"
      />
      <Text
        className="text-xs font-semibold text-onyx text-center mt-1.5 mb-0.5 w-full"
        numberOfLines={1}
      >
        {entry?.displayName}
      </Text>
      <Text className="text-xs text-slate mb-1.5" numberOfLines={1}>
        {formattedPoints}
      </Text>
      <View className={config.badgeClass}>
        <Text className={config.rankTextClass}>{entry?.rank}</Text>
      </View>
    </View>
  );
};

export const LeaderboardWidget = ({ onSeeAll }: LeaderboardWidgetProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const { data, isLoading } = useLeaderboardSummaryQuery(3);

  // Server order; ties share a rank (1, 1, 3), so take the first three by position.
  const podiumOrder: RankedLeaderboardEntry[] = (data?.entries ?? []).slice(0, 3);

  // The server always returns the caller's own standing, even outside the top 3.
  const currentUser = data?.me ?? null;
  const currentUserPoints = formatCompactNumber(currentUser?.points);
  const currentUserRank = currentUser?.rank ? `${currentUser.rank}` : "–";

  return (
    <View className="rounded-2xl border border-ash-3 bg-chalk p-4">
      {/* Header */}
      <View className="flex-row items-center justify-between mb-4 -mx-4 -mt-4 px-4 py-3 bg-primary-1 rounded-t-2xl">
        <Text className="text-sm font-bold text-primary">
          {t("LABEL_LEADERBOARD")}
        </Text>
        <Pressable
          onPress={onSeeAll}
          className="px-2 py-1 active:opacity-70 web:cursor-pointer hover:bg-ash-3 hover:rounded-lg"
        >
          <Text className="text-xs text-primary">{t("LABEL_SEE_ALL")}</Text>
        </Pressable>
      </View>
      {/* Loading */}
      {isLoading && <LeaderboardWidgetSkeleton />}

      {/* Top 3 podium */}
      {!isLoading && podiumOrder.length > 0 && (
        <View className="flex-row justify-between items-end px-1 mb-4 overflow-hidden">
          {podiumOrder.map((podiumEntry) => (
            <TopUserCard key={podiumEntry.userId} entry={podiumEntry} />
          ))}
        </View>
      )}

      {!isLoading && (
        <>
          <View className="h-px bg-ash-3 mb-3" />

          {/* Current user row */}
          {currentUser ? (
            <View className="flex-row items-center justify-between bg-ash border border-ash-3 rounded-xl p-3 gap-2">
              {/* Avatar + name */}
              <View className="flex-row items-center gap-2 flex-1 min-w-0">
                <BlocksAvatar
                  userId={currentUser.userId}
                  fallbackImageUrl={currentUser.avatar}
                  name={currentUser.displayName}
                  size={36}
                  className="shrink-0"
                />
                <Text
                  className="text-sm font-semibold text-onyx flex-1"
                  numberOfLines={1}
                >
                  {t("LABEL_YOU")}
                </Text>
              </View>

              {/* Stats */}
              <View className="flex-row items-center gap-3 shrink-0">
                {/* Points */}
                <View className="items-start">
                  <Text className="text-xs text-slate">
                    {t("LABEL_COL_POINTS")}
                  </Text>
                  <View className="flex-row items-center gap-1">
                    <Text className="text-sm font-bold text-onyx">
                      {currentUserPoints}
                    </Text>
                    <MedalStar
                      size={16}
                      color={themeVars["--color-primary-default"]}
                      variant="Bold"
                    />
                  </View>
                </View>

                {/* Divider */}
                <View className="w-px h-8 bg-ash-3" />

                {/* Rank */}
                <View className="items-start">
                  <Text className="text-xs text-slate">{t("LABEL_RANK")}</Text>
                  <View className="flex-row items-center gap-1">
                    <Text className="text-sm font-bold text-onyx">
                      #{currentUserRank}
                    </Text>
                    <Ranking
                      size={16}
                      color={themeVars["--color-primary-default"]}
                      variant="Bold"
                    />
                  </View>
                </View>
              </View>
            </View>
          ) : (
            <View className="items-center py-2">
              <Text className="text-xs text-slate">
                {t("LABEL_NO_LEADERBOARD_DATA")}
              </Text>
            </View>
          )}
        </>
      )}
    </View>
  );
};

export default LeaderboardWidget;
